import React, { forwardRef, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { useHighlightedField } from '../FieldHighlight/context'
import { getStatusIcon } from '../statusIcon'

import classes from './styles.module.scss'
import classNames from 'classnames'

const VARIANT = {
  OUTLINED: 'outlined',
  SIMPLE: 'simple'
}

interface ITextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  name: string
  fullWidth?: boolean
  required?: boolean
  errors?: FieldErrors
  isSuccess?: boolean
  isWarning?: boolean
  isError?: boolean
  canPasteField?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isLoading?: boolean
  withError?: boolean
  error?: string
  variant?: typeof VARIANT[keyof typeof VARIANT]
}

interface IRightIconProps {
  rightIcon: React.ReactNode
  handleTogglePassword: () => void
  type: HTMLInputElement['type']
  isPassword: boolean
  isError: boolean
  isWarning: boolean
  isSuccess: boolean
  isLoading?: boolean
}

const TextInput = forwardRef((props: ITextInputProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    type = 'text',
    label,
    name,
    fullWidth,
    errors,
    disabled,
    isError = false,
    isWarning = false,
    isSuccess = false,
    required = false,
    leftIcon,
    rightIcon,
    canPasteField = true,
    isLoading = false,
    withError = true,
    variant = VARIANT.SIMPLE,
    error,
    onPaste,
    ...rest
  } = props
  const isPassword = type === 'password'
  const [inputType, setInputType] = useState<HTMLInputElement['type']>(type)
  // Keyed on the error itself, not its text: a rule with no configured message
  // would otherwise block the submit with nothing on screen to explain it.
  const hasError = isError || !!errors?.[name]
  const highlightedField = useHighlightedField()
  // A field the URL points at borrows the warning look rather than inventing one.
  const hasWarning = isWarning || highlightedField === name

  const handleTogglePassword = () => {
    setInputType(inputType === 'password' ? 'text' : 'password')
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!canPasteField) {
      e.preventDefault()
      return
    }

    if (onPaste) {
      onPaste(e)
    }
  }

  return (
    <div className={classNames({
      [classes.container]: true,
      [classes.fullWidth]: fullWidth,
      [classes.withError]: withError,
      [classes.input__error]: hasError,
      [classes.input__warning]: hasWarning,
      [classes.input__success]: isSuccess,
      [classes.input__loading]: isLoading,
    })}>
      {label &&
        <label
          className={classNames({
            [classes.label]: true,
            [classes.label__disabled]: disabled
          })}
          htmlFor={name}
        >
          {label + (required ? ' *' : '')}
        </label>
      }
      <div className={classes.input__container}>
        <LeftIcon leftIcon={leftIcon} />
        <input
          className={classNames({
            [classes.input]: true,
            [classes.input__outlined]: variant === VARIANT.OUTLINED,
            [classes.input__leftSpace]: leftIcon,
            [classes.input__rightSpace]: (rightIcon || isPassword || isLoading || hasError || isSuccess || hasWarning),
          })}
          type={inputType}
          disabled={disabled}
          id={name}
          name={name}
          ref={ref}
          onPaste={handlePaste}
          {...rest}
        />
        <RightIcon
          handleTogglePassword={handleTogglePassword}
          rightIcon={rightIcon}
          isPassword={isPassword}
          isError={hasError}
          isWarning={hasWarning}
          isSuccess={isSuccess}
          type={inputType}
          isLoading={isLoading}
        />
      </div>
      {((hasError || error) && withError !== false) && (
        <span className={classes.error}>
          {errors?.[name]?.message as string || error}
        </span>
      )}
    </div>
  )
})

const LeftIcon = ({ leftIcon }: { leftIcon: React.ReactNode }) => {
  return (
    <span className={classes.input__left}>
      {leftIcon}
    </span>
  )
}

const RightIcon = (props: IRightIconProps) => {
  const {
    handleTogglePassword,
    rightIcon,
    type,
    isPassword,
    isError,
    isWarning,
    isSuccess,
    isLoading
  } = props
  const statusIcon = getStatusIcon({ isError, isWarning, isSuccess })

  return (
    <span className={classes.input__right}>
      {isPassword ?
        <span className={classes.input__password}>
          <FontAwesomeIcon
            onClick={handleTogglePassword}
            data-testid="toggle-password-on"
            className={classNames({
              [classes.show]: type === 'password',
              [classes.hide]: type !== 'password'
            })}
            icon={faEye}
          />
          <FontAwesomeIcon
            onClick={handleTogglePassword}
            data-testid="toggle-password-off"
            className={classNames({
              [classes.show]: type !== 'password',
              [classes.hide]: type === 'password'
            })}
            icon={faEyeSlash}
          />
        </span> :
        <>
          {statusIcon ?
            <FontAwesomeIcon icon={statusIcon} className={classes.statusIcon} /> :
            isLoading ?
              <div className={classes.spinner} /> :
              <>{rightIcon}</>
          }
        </>
      }
    </span>
  )
}

TextInput.displayName = 'TextInput'

export default TextInput